/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnDestroy, type OnInit, Component } from '@angular/core';
import { type HttpResponse, type Polling, ExtendService, I18nService, PollingService } from '@dm/core';
import { ToolService } from 'app/common/services/tool.service';
import { type PmsStatus } from 'app/common/types/mo';
import { delay } from 'lodash';
import { type Observable, type Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { ScheduleReportService } from '../../../monitor/schedule-report/schedule-report.service';
import { PermissionCheckObjE } from '../smart-assistant.const';
import { SmartAssistantService } from '../smart-assistant.service';
import { type ReportTask, type TaskLog, type TaskLogRes } from './assistant-report';
import { REPORT_COUNT } from './assistant-report.const';

@Component({
    selector: 'app-smart-assistant-report',
    templateUrl: './assistant-report.component.html',
    styleUrls: ['./assistant-report.component.scss'],
})
export class SmartAssistantReportComponent implements OnInit, OnDestroy {
    i18n = {
        helpTitle: this.i18nService.get('monitor.Report_simplifiedMode_export_title'),
        exportReport: this.i18nService.get('monitor.Report_simplifiedMode_export_btn'),
    };

    /**
     * 展示的报告数据
     */
    reportDatas: TaskLog[] = [];
    /**
     * 是否处于加载中
     */
    loading = true;
    /**
     * 查询报告轮询
     */
    taskPolling$: Polling;
    /**
     * 轮询的订阅
     */
    taskSub$: Subscription;
    /**
     * 有报告读取权限权限
     */
    hasReportReadPermission: boolean;

    constructor(
        private i18nService: I18nService,
        private service: ScheduleReportService,
        private extendService: ExtendService,
        private pollingService: PollingService,
        private toolService: ToolService,
        private smartAssistantService: SmartAssistantService,
    ) { }

    ngOnInit(): void {
        // 没有“报告读取”权限，就不展示下面的对象了
        this.hasReportReadPermission = this.smartAssistantService.checkPermission(PermissionCheckObjE.Report);
        if (!this.hasReportReadPermission) {
            this.loading = false;
            return;
        }

        // 查询是否配置PMS LUN
        this.toolService.checkPmsLunStatus().subscribe(
            (data: PmsStatus) => {
                if (data && data.selected) {
                    this.initTaskData();
                    return;
                }
                this.loading = false;
            }, () => {
                this.loading = false;
            },
        );
    }

    initTaskData(): void {
        this.service.getTask().subscribe(({ data }: { data: ReportTask[] }) => {
            if (data && data.length) {
                this.initReportData(data, true);
            } else {
                this.reportDatas = [];
                this.loading = false;
            }
        }, () => {
            this.reportDatas = [];
            this.loading = false;
        });
    }

    initReportData(taskData: ReportTask[], isFirst?: boolean): void {
        // 过滤有报告的任务
        const filterTaskData = taskData.filter((task) => task.export_count > 0);
        if (filterTaskData.length === 0) {
            // 获取回来的任务都没有报告下载
            this.reportDatas = [];
            this.loading = false;
            return;
        }

        // 只获取前三个任务日志
        const taskIds = filterTaskData.slice(0, REPORT_COUNT).map((task) => task.id);
        const promise: Observable<HttpResponse>[] = [];
        taskIds.forEach((taskId: string) => {
            const getReportFileTask = this.service.getReportFile(taskId, { mask: false });
            promise.push(getReportFileTask);
        });
        this.extendService.forkAll(promise).pipe(
            map((reportRes: TaskLogRes[]) => {
                const logData: TaskLog[] = [];

                // 每个任务获取最新的一条报告
                reportRes.forEach(({ data }: { data: TaskLog[] }) => {
                    if (data && data[0]) {
                        logData.push(data[0]);
                    }
                });
                this.reportDatas = logData;
                if (isFirst) {
                    this.loading = false;
                    delay(this.setPolling, 15000);
                }
            }),
        ).subscribe();
    }

    /**
     * 设置任务的轮询
     */
    setPolling = (): void => {
        this.taskPolling$ = this.pollingService.add(this.service.getTaskRest(), 15);
        this.taskSub$ = this.taskPolling$.subscribe(({ data }: { data: ReportTask[] }) => {
            if (data && data.length) {
                this.initReportData(data);
            } else {
                this.reportDatas = [];
            }
        });
    };

    /**
     * 导出报告
     */
    exportReport(report): void {
        this.loading = true;
        this.service.exportFile(report.task_id, report.log_id, report.file_name).subscribe(() => {
            this.loading = false;
        }, () => {
            this.loading = false;
        });
    }

    ngOnDestroy(): void {
        if (this.taskPolling$) {
            this.pollingService.remove(this.taskPolling$);
        }
        this.taskSub$?.unsubscribe();
    }
}
